import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

/** Answer to a sign-in code request: a code was sent, or the address has no invite yet. */
export type EmailStartResult = {
  status: 'sent' | 'not_invited'
  message: string
  challenge_id?: string
  resend_after_s?: number
  request_access_path?: string
}

export type AfosUser = {
  email: string
  name: string
  picture?: string
  provider: 'google' | 'cognito' | 'demo' | 'email_otp'
  customer_id?: string
  project_id?: string
  project_namespace?: string
  user_id?: string
  roles?: string[]
  permissions?: string[]
  is_super_admin?: boolean
  can_invite?: boolean
  two_factor_enabled?: boolean
}

type CognitoPublicStatus = {
  enabled: boolean
  region?: string | null
  user_pool_id_set?: boolean
  client_id_set?: boolean
  domain_set?: boolean
  otp_policy?: string
}

export type AuthPosture = {
  mode: string
  tone: string
  banner: string
  demo_allowed: boolean
  sso_enforced: boolean
  is_demo_session?: boolean
  email_code_ready?: boolean
  sign_in_methods?: string[]
}

type AuthState = {
  user: AfosUser | null
  loading: boolean
  googleClientId: string | null
  cognito: CognitoPublicStatus | null
  otpPolicy: string
  authPosture: AuthPosture | null
  emailSignIn: boolean
  startEmailSignIn: (email: string) => Promise<EmailStartResult>
  verifyEmailSignIn: (
    email: string,
    challengeId: string,
    otp: string,
    previousChallengeIds?: string[],
  ) => Promise<void>
  redeemEmailLink: (email: string, challengeId: string, token: string) => Promise<void>
  signInWithGoogleCredential: (credential: string) => Promise<void>
  signInWithCognitoIdToken: (idToken: string) => Promise<void>
  signInDemo: () => Promise<void>
  signOut: () => Promise<void>
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)
const STORAGE_KEY = 'afos360.session'

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    ...init,
  })
  if (!res.ok) {
    const detail = await res.text()
    throw new Error(detail || res.statusText)
  }
  return res.json() as Promise<T>
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AfosUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [googleClientId, setGoogleClientId] = useState<string | null>(null)
  const [cognito, setCognito] = useState<CognitoPublicStatus | null>(null)
  const [otpPolicy, setOtpPolicy] = useState('sendgrid_step_up')
  const [authPosture, setAuthPosture] = useState<AuthPosture | null>(null)
  const [emailSignIn, setEmailSignIn] = useState(false)

  const refresh = useCallback(async () => {
    try {
      const me = await api<{
        user: AfosUser | null
        google_client_id: string | null
        cognito?: CognitoPublicStatus
        otp_policy?: string
        auth_posture?: AuthPosture
        email_sign_in?: boolean
      }>('/api/afos360/auth/me')
      setUser(me.user)
      setGoogleClientId(me.google_client_id)
      setCognito(me.cognito ?? null)
      setOtpPolicy(me.otp_policy || 'sendgrid_step_up')
      setAuthPosture(me.auth_posture ?? null)
      setEmailSignIn(Boolean(me.email_sign_in))
      if (me.user) localStorage.setItem(STORAGE_KEY, JSON.stringify(me.user))
      else localStorage.removeItem(STORAGE_KEY)
    } catch {
      const cached = localStorage.getItem(STORAGE_KEY)
      if (cached) {
        try {
          setUser(JSON.parse(cached) as AfosUser)
        } catch {
          setUser(null)
        }
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const signInWithGoogleCredential = useCallback(async (credential: string) => {
    const me = await api<{ user: AfosUser }>('/api/afos360/auth/google', {
      method: 'POST',
      body: JSON.stringify({ credential }),
    })
    setUser(me.user)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(me.user))
  }, [])

  const signInWithCognitoIdToken = useCallback(async (idToken: string) => {
    const me = await api<{ user: AfosUser }>('/api/afos360/auth/cognito', {
      method: 'POST',
      body: JSON.stringify({ id_token: idToken }),
    })
    setUser(me.user)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(me.user))
  }, [])

  const startEmailSignIn = useCallback(async (email: string) => {
    return api<EmailStartResult>('/api/afos360/auth/email/start', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  }, [])

  const redeemEmailLink = useCallback(async (email: string, challengeId: string, token: string) => {
    const me = await api<{ user: AfosUser }>('/api/afos360/auth/email/link', {
      method: 'POST',
      body: JSON.stringify({ email, challenge_id: challengeId, token }),
    })
    setUser(me.user)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(me.user))
  }, [])

  const verifyEmailSignIn = useCallback(
    async (email: string, challengeId: string, otp: string, previousChallengeIds: string[] = []) => {
    const me = await api<{ user: AfosUser }>('/api/afos360/auth/email/verify', {
      method: 'POST',
      body: JSON.stringify({
        email,
        challenge_id: challengeId,
        otp,
        previous_challenge_ids: previousChallengeIds.slice(0, 4),
      }),
    })
    setUser(me.user)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(me.user))
  }, [])

  const signInDemo = useCallback(async () => {
    const me = await api<{ user: AfosUser }>('/api/afos360/auth/demo', { method: 'POST' })
    setUser(me.user)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(me.user))
  }, [])

  const signOut = useCallback(async () => {
    try {
      await api('/api/afos360/auth/logout', { method: 'POST' })
    } catch {
      /* offline logout still clears local */
    }
    localStorage.removeItem(STORAGE_KEY)
    setUser(null)
  }, [])

  const value = useMemo(
    () => ({
      user,
      loading,
      googleClientId,
      cognito,
      otpPolicy,
      authPosture,
      emailSignIn,
      startEmailSignIn,
      verifyEmailSignIn,
      redeemEmailLink,
      signInWithGoogleCredential,
      signInWithCognitoIdToken,
      signInDemo,
      signOut,
      refresh,
    }),
    [
      user,
      loading,
      googleClientId,
      cognito,
      otpPolicy,
      authPosture,
      emailSignIn,
      startEmailSignIn,
      verifyEmailSignIn,
      redeemEmailLink,
      signInWithGoogleCredential,
      signInWithCognitoIdToken,
      signInDemo,
      signOut,
      refresh,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth requires AuthProvider')
  return ctx
}
